import type { JobCode, SchedulerDepartment } from 'core/API/scheduler';

export const DEPARTMENT_NONE_VALUE = 'none';
export const MAX_DEPARTMENTS = 10;
export const POSITION_DELETE_BLOCKED_MESSAGE = 'Remove positions from schedule to delete';

export const SCHEDULER_POSITIONS_QUERY_KEY = ['schedulerPositions'] as const;

export const isDepartmentNameValid = (
	name: string,
	existingDepartments: SchedulerDepartment[],
	excludeId?: number
): string | null => {
	const trimmed = name.trim();
	if (!trimmed) return 'Department name is required';
	if (trimmed.toLowerCase() === 'none') return '"None" is not a valid department name';
	const duplicate = existingDepartments.some(
		(d) => d.name.toLowerCase() === trimmed.toLowerCase() && d.id !== excludeId
	);
	if (duplicate) return 'Department name must be unique';
	return null;
};

export const getJobCodeDepartmentId = (jobCode: JobCode | null | undefined): number | null => {
	if (!jobCode?.department) return null;
	if (typeof jobCode.department === 'string') {
		const match = jobCode.department.match(/\/(\d+)$/);
		return match ? Number(match[1]) : null;
	}
	return jobCode.department.id ?? null;
};

export const sortDepartmentsAlphabetically = <T extends { name: string }>(departments: T[]): T[] =>
	[...departments].sort((a, b) => a.name.localeCompare(b.name));

export const filterJobCodesByDepartments = (
	jobCodes: JobCode[],
	selectedDepartmentIds: number[] | 'all'
): JobCode[] => {
	if (selectedDepartmentIds === 'all') return jobCodes;
	return jobCodes.filter((jc) => {
		const deptId = getJobCodeDepartmentId(jc);
		return deptId !== null && selectedDepartmentIds.includes(deptId);
	});
};

export const isJobCodeReferencedByDepartment = (
	jobCodes: JobCode[],
	departmentId: number
): boolean => jobCodes.some((jc) => getJobCodeDepartmentId(jc) === departmentId);

export const buildDepartmentIri = (departmentId: number): string =>
	`api/departments/${departmentId}`;

export const buildPositionIri = (positionId: number): string =>
	`api/positions/${positionId}`;

export const NONE_POSITION_OPTION = { id: 0, '@id': 'none', name: 'None' } as const;

export const positionMatchesJobCode = (
	position: { jobCode?: JobCode | string | null },
	jobCodeIri: string | null | undefined,
	jobCodeId?: number | string | null
): boolean => {
	if (!position.jobCode) return false;
	if (typeof position.jobCode === 'string') {
		return Boolean(jobCodeIri) && position.jobCode === jobCodeIri;
	}
	if (jobCodeIri && position.jobCode['@id'] === jobCodeIri) return true;
	return jobCodeId != null && String(position.jobCode.id) === String(jobCodeId);
};

export const filterPositionsByJobCode = <
	T extends { name: string; jobCode?: JobCode | string | null },
>(
	positions: T[],
	jobCodeIri: string | null | undefined,
	jobCodeId?: number | string | null
): T[] => {
	if (!jobCodeIri && jobCodeId == null) return [];
	return positions
		.filter((position) => positionMatchesJobCode(position, jobCodeIri, jobCodeId))
		.sort((a, b) => a.name.localeCompare(b.name));
};

export const resolvePositionSelection = (
	shiftPosition: string | { id: number; '@id': string; name?: string } | null | undefined,
	positions: Array<{ id: number; '@id': string; name: string }>
) => {
	if (!shiftPosition) return NONE_POSITION_OPTION;
	if (typeof shiftPosition === 'string') {
		return positions.find((position) => position['@id'] === shiftPosition) || NONE_POSITION_OPTION;
	}
	return positions.find((position) => position.id === shiftPosition.id) || shiftPosition;
};

export const getPositionJobCodeId = (position: { jobCode?: JobCode | string | null }): number | null => {
	if (!position.jobCode) return null;
	if (typeof position.jobCode === 'string') {
		const match = position.jobCode.match(/\/(\d+)$/);
		return match ? Number(match[1]) : null;
	}
	return position.jobCode.id ?? null;
};
